/**
 * Capacity Tasks Module
 * Handles task management, presets, and task operations
 */
export class CapacityTasks {
  constructor(dataManager) {
    this.data = dataManager;
    this.tasks = [];
  }

  loadTasks() {
    this.tasks = this.data.get('enoughTasks', []);
  }

  saveTasks() {
    this.data.set('enoughTasks', this.tasks);
  }

  addTask(taskData) {
    const task = {
      id: this.generateTaskId(),
      name: taskData.name || '',
      duration: Math.max(1, Math.min(480, taskData.duration || 15)),
      category: taskData.category || 'general',
      energyType: taskData.energyType || 'mental',
      energyLevel: taskData.energyLevel || 1,
      completed: false,
      createdAt: new Date().toISOString(),
      barriers: taskData.barriers || [],
      notes: taskData.notes || '',
      priority: taskData.priority || 'medium',
    };

    this.tasks.push(task);
    this.saveTasks();
    return task;
  }

  updateTask(taskId, updates) {
    const taskIndex = this.tasks.findIndex(t => t.id === taskId);
    if (taskIndex >= 0) {
      this.tasks[taskIndex] = { ...this.tasks[taskIndex], ...updates };
      this.saveTasks();
      return this.tasks[taskIndex];
    }
    return null;
  }

  deleteTask(taskId) {
    this.tasks = this.tasks.filter(t => t.id !== taskId);
    this.saveTasks();
  }

  toggleTaskCompletion(taskId) {
    const task = this.tasks.find(t => t.id === taskId);
    if (task) {
      task.completed = !task.completed;
      task.completedAt = task.completed ? new Date().toISOString() : null;
      this.saveTasks();
      return task;
    }
    return null;
  }

  getActiveTasks() {
    return this.tasks.filter(task => !task.archived);
  }

  getCompletedTasks() {
    return this.tasks.filter(task => task.completed && !task.archived);
  }

  getTaskSuggestions() {
    const currentHour = new Date().getHours();
    const suggestions = [];

    // Morning suggestions (6-12)
    if (currentHour >= 6 && currentHour < 12) {
      suggestions.push(
        { name: 'Morning routine', duration: 60, category: 'personal' },
        { name: 'Check emails', duration: 30, category: 'work' },
        { name: 'Plan the day', duration: 20, category: 'planning' }
      );
    }

    // Afternoon suggestions (12-18)
    if (currentHour >= 12 && currentHour < 18) {
      suggestions.push(
        { name: 'Deep work block', duration: 90, category: 'work' },
        { name: 'Exercise break', duration: 45, category: 'health' },
        { name: 'Meeting prep', duration: 30, category: 'work' }
      );
    }

    // Evening suggestions (18-24)
    if (currentHour >= 18 || currentHour < 6) {
      suggestions.push(
        { name: 'Wind down routine', duration: 45, category: 'personal' },
        { name: 'Review accomplishments', duration: 15, category: 'reflection' },
        { name: 'Prepare for tomorrow', duration: 30, category: 'planning' }
      );
    }

    return suggestions;
  }

  generateTaskId() {
    return `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  getTaskPresets() {
    return {
      morning: {
        name: 'Morning Routine',
        tasks: [
          {
            name: 'Make Breakfast',
            duration: 45,
            icon: '🍳',
            note: 'Including decisions, prep, cooking, eating, cleanup',
          },
          {
            name: 'Take Shower',
            duration: 35,
            icon: '🚿',
            note: 'Including gathering supplies, actual shower, post-shower routine',
          },
          {
            name: 'Get Dressed',
            duration: 25,
            icon: '👔',
            note: 'Including outfit decisions, finding items, weather check',
          },
          {
            name: 'Morning Meds',
            duration: 15,
            icon: '💊',
            note: 'Including remembering, finding, taking, tracking',
          },
          {
            name: 'Check Weather',
            duration: 10,
            icon: '🌤️',
            note: 'Plus outfit adjustments if needed',
          },
          {
            name: 'Pack Bag/Lunch',
            duration: 20,
            icon: '🎒',
            note: 'Including finding items, decision-making, double-checking',
          },
        ],
      },
      communication: {
        name: 'Communication & Admin',
        tasks: [
          {
            name: 'Check Emails',
            duration: 30,
            icon: '📧',
            note: 'Including reading, processing, responding, filing',
          },
          {
            name: 'Quick Call (Known)',
            duration: 25,
            icon: '📞',
            note: 'Including phone anxiety prep and post-call processing',
          },
          {
            name: 'Doctor Call',
            duration: 45,
            icon: '🏥',
            note: 'Including prep, hold time, form anxiety, note-taking',
          },
          {
            name: 'Review Calendar',
            duration: 15,
            icon: '📅',
            note: 'Including anxiety about upcoming tasks, scheduling conflicts',
          },
          {
            name: 'Text Responses',
            duration: 20,
            icon: '💬',
            note: 'Including reading, overthinking responses, sending',
          },
          {
            name: 'Online Form',
            duration: 60,
            icon: '📋',
            note: 'Including gathering info, decision paralysis, double-checking',
          },
        ],
      },
      household: {
        name: 'Household Tasks',
        tasks: [
          {
            name: 'Tidy One Room',
            duration: 45,
            icon: '🧹',
            note: 'Including getting distracted by items found, organizing',
          },
          {
            name: 'Do Dishes',
            duration: 35,
            icon: '🍽️',
            note: 'Including clearing space, washing, drying, putting away',
          },
          {
            name: 'Laundry Load',
            duration: 90,
            icon: '👕',
            note: 'Including sorting, washing, folding, putting away',
          },
          {
            name: 'Grocery Planning',
            duration: 30,
            icon: '🛒',
            note: 'Including checking inventory, making list, route planning',
          },
          {
            name: 'Bill Payment',
            duration: 25,
            icon: '💳',
            note: 'Including gathering info, double-checking, confirmation anxiety',
          },
          {
            name: 'Plant Care',
            duration: 20,
            icon: '🌱',
            note: 'Including checking needs, watering, pruning, cleaning up',
          },
        ],
      },
      work: {
        name: 'Work Tasks',
        tasks: [
          {
            name: 'Deep Focus Block',
            duration: 90,
            icon: '🎯',
            note: 'Uninterrupted focused work on complex tasks',
          },
          {
            name: 'Meeting (1 hour)',
            duration: 75,
            icon: '👥',
            note: 'Including prep, actual meeting, follow-up notes',
          },
          {
            name: 'Code Review',
            duration: 45,
            icon: '💻',
            note: 'Including reading code, thinking through implications, writing feedback',
          },
          {
            name: 'Client Call',
            duration: 60,
            icon: '📞',
            note: 'Including prep, call, note-taking, follow-up anxiety',
          },
          {
            name: 'Project Planning',
            duration: 60,
            icon: '📋',
            note: 'Including research, outlining, breaking down tasks, estimating',
          },
          {
            name: 'Documentation',
            duration: 45,
            icon: '📚',
            note: 'Including organizing thoughts, writing clearly, formatting',
          },
        ],
      },
      selfcare: {
        name: 'Self-Care & Health',
        tasks: [
          {
            name: 'Exercise Session',
            duration: 60,
            icon: '🏃‍♀️',
            note: 'Including changing, warm-up, activity, cool-down, shower',
          },
          {
            name: 'Meditation',
            duration: 20,
            icon: '🧘‍♀️',
            note: 'Including setup, practice, transition back to day',
          },
          {
            name: 'Therapy Session',
            duration: 60,
            icon: '🛋️',
            note: 'Including travel, session, processing time',
          },
          {
            name: 'Reading Time',
            duration: 45,
            icon: '📖',
            note: 'Including choosing book, reading, reflecting',
          },
          {
            name: 'Journaling',
            duration: 30,
            icon: '✍️',
            note: 'Including gathering thoughts, writing, organizing entries',
          },
          {
            name: 'Hobby Time',
            duration: 60,
            icon: '🎨',
            note: 'Including setup, creative flow, cleanup',
          },
        ],
      },
    };
  }

  addTaskFromSuggestion(task, duration) {
    return this.addTask({
      name: task.name,
      duration: duration || task.duration,
      category: task.category || 'general',
      energyType: this.inferEnergyType(task.name),
      energyLevel: 1,
    });
  }

  inferEnergyType(taskName) {
    const name = taskName.toLowerCase();

    if (name.includes('exercise') || name.includes('workout') || name.includes('physical')) {
      return 'physical';
    }
    if (name.includes('meeting') || name.includes('call') || name.includes('social')) {
      return 'social';
    }
    if (name.includes('creative') || name.includes('design') || name.includes('art')) {
      return 'creative';
    }
    if (name.includes('emotional') || name.includes('therapy') || name.includes('feelings')) {
      return 'emotional';
    }
    if (name.includes('admin') || name.includes('organizing') || name.includes('filing')) {
      return 'administrative';
    }

    return 'mental'; // Default
  }

  // Task choice modal functionality
  showTaskAdditionChoice() {
    const modal = document.createElement('div');
    modal.className = 'task-choice-modal';
    modal.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="choice-container">
        <div class="choice-header">
          <h3>Add a Task</h3>
          <p>Choose how you'd like to add your task</p>
          <button class="modal-close" data-action="close">×</button>
        </div>

        <div class="choice-options">
          <button class="choice-option quick-add" data-choice="quick">
            <div class="choice-icon">⚡</div>
            <div class="choice-content">
              <h4>Quick Add</h4>
              <p>Fast entry with suggested presets</p>
            </div>
          </button>

          <button class="choice-option calculator-add" data-choice="calculator">
            <div class="choice-icon">🧠</div>
            <div class="choice-content">
              <h4>ADHD Calculator</h4>
              <p>Smart capacity estimation with barriers</p>
            </div>
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Event handlers
    modal.addEventListener('click', (e) => {
      if (e.target.classList.contains('modal-backdrop') || e.target.dataset.action === 'close') {
        document.body.removeChild(modal);
        return;
      }

      const choice = e.target.closest('[data-choice]');
      if (choice) {
        const choiceType = choice.dataset.choice;
        document.body.removeChild(modal);

        if (choiceType === 'quick') {
          this.openQuickAddModal();
        } else if (choiceType === 'calculator') {
          this.openCapacityCalculator();
        }
      }
    });
  }

  openQuickAddModal() {
    const modal = document.createElement('div');
    modal.className = 'quick-add-modal';
    modal.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="quick-add-container">
        <div class="quick-add-header">
          <h3>Quick Add Task</h3>
          <button class="modal-close" data-action="close">×</button>
        </div>

        <div class="quick-add-content">
          <div class="task-input-section">
            <label for="quick-task-name">Task name:</label>
            <input type="text" id="quick-task-name" placeholder="e.g., Check emails" class="quick-input">

            <label for="quick-duration">Duration (minutes):</label>
            <input type="number" id="quick-duration" value="15" min="1" max="480" class="quick-input">

            <label for="energy-type">Energy type:</label>
            <select id="energy-type" class="quick-input">
              <option value="mental" selected>🧠 Mental - Thinking, planning, problem-solving</option>
              <option value="physical">💪 Physical - Moving, exercising, manual work</option>
              <option value="social">👥 Social - Interacting with people, meetings</option>
              <option value="creative">🎨 Creative - Designing, writing, artistic work</option>
              <option value="emotional">❤️ Emotional - Processing feelings, relationships</option>
              <option value="administrative">📋 Administrative - Organizing, filing, routine tasks</option>
            </select>

            <label for="energy-level">Current energy level:</label>
            <select id="energy-level" class="quick-input">
              <option value="1">🔋 High - I'm feeling sharp and focused</option>
              <option value="1.5" selected>⚡ Medium - I'm doing okay, typical energy</option>
              <option value="2.5">🪫 Low - I'm tired, things feel harder</option>
              <option value="4">😵 Very Low - I'm burnt out, everything feels overwhelming</option>
            </select>
            <p class="energy-note">This adjusts how much capacity the task will actually use based on your current state</p>
          </div>

          <div class="presets-section">
            <div class="presets-header">
              <h4>Quick Presets</h4>
              <p>Click any preset to auto-fill</p>
            </div>
            <div class="presets-grid" id="presets-grid">
              ${this.renderPresetButtons()}
            </div>
          </div>

          <div class="quick-add-actions">
            <button class="btn btn--secondary" data-action="cancel">Cancel</button>
            <button class="btn btn--primary" data-action="add-task">Add Task</button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    this.initQuickAddModal(modal);
  }

  renderPresetButtons() {
    const presets = this.getTaskPresets();
    return Object.entries(presets).map(([category, preset]) => `
      <div class="preset-category">
        <h5 class="preset-category-title">${preset.name}</h5>
        <div class="preset-buttons">
          ${preset.tasks.map(task => `
            <button class="preset-button" data-preset="${category}" data-task="${task.name}">
              <span class="preset-icon">${task.icon}</span>
              <span class="preset-name">${task.name}</span>
              <span class="preset-duration">${task.duration}m</span>
            </button>
          `).join('')}
        </div>
      </div>
    `).join('');
  }

  initQuickAddModal(modal) {
    const taskNameInput = modal.querySelector('#quick-task-name');
    const durationInput = modal.querySelector('#quick-duration');
    const energyTypeSelect = modal.querySelector('#energy-type');
    const energyLevelSelect = modal.querySelector('#energy-level');
    const addButton = modal.querySelector('[data-action="add-task"]');

    // Preset button handlers
    const presetButtons = modal.querySelectorAll('.preset-button');
    presetButtons.forEach(button => {
      button.addEventListener('click', () => {
        const category = button.dataset.preset;
        const taskName = button.dataset.task;
        const presets = this.getTaskPresets();
        const task = presets[category].tasks.find(t => t.name === taskName);

        if (task) {
          taskNameInput.value = task.name;
          durationInput.value = task.duration;
        }
      });
    });

    // Add task handler
    addButton.addEventListener('click', () => {
      const taskData = {
        name: taskNameInput.value.trim(),
        duration: parseInt(durationInput.value, 10),
        energyType: energyTypeSelect.value,
        energyLevel: parseFloat(energyLevelSelect.value),
      };

      if (taskData.name && taskData.duration > 0) {
        this.addTask(taskData);
        document.body.removeChild(modal);
      }
    });

    // Cancel handler
    modal.addEventListener('click', (e) => {
      if (e.target.classList.contains('modal-backdrop') ||
          e.target.dataset.action === 'close' ||
          e.target.dataset.action === 'cancel') {
        document.body.removeChild(modal);
      }
    });
  }
}
